import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { setBatchLoadingStatus } from "../../../api_calls/requestConfig";
import { checkUserRole } from "../../../api_calls/userManagement";
import { loadRetailerAccessAvailability } from "../../../api_calls/retailerSelection";
import { RETAILER_SELECTION_OPTIONS_KEY } from "../../../api_calls/retailerSelectionConfig";
import { deleteItems, updateStateData } from "../../../Store/actions";
import GroupedMultiSelect from "../Selectors/GroupedMultiSelect";
import MultiSelect from "../Selectors/MultiSelect";
import {
  ROOT_RETAILER_GROUP_PARAM,
  STORE_FILTER_MODES,
  WORKSPACE_OPTIONS_KEY,
  loadRequestedStores,
  resolveBehaviorMode,
  resolveDateRangeFromStores,
  resolveLoggedInUserType,
  resolveSelectedStoreNames,
  resolveStoreIds,
  resolveStoreNames,
  resolveStoreRecordsForOptions,
  shouldUseRetailerStoreHierarchy,
  toStoreOptions,
  toStoreOptionsWithRootRetailer,
  uniqueStoreNames,
} from "./storeFilterUtils";

const StoreFilterSelect = ({
  behaviorMode,
  fetchListFor,
  mode,
  reduxKeywordToSaveValue,
  usedFor,
}) => {
  const storeDetails = useSelector((state) => state.userSelections);
  const dispatchAction = useDispatch();

  const [changesMade, setChangesMade] = React.useState(false);
  const [storesSelection, setStoreSelection] = React.useState([]);
  const [storeNameOptionSelected, setStoreNameOptionsSelected] = React.useState([]);
  const [storesLoaded, setStoresLoaded] = React.useState(false);
  const [retailerAccessLoaded, setRetailerAccessLoaded] = React.useState(false);
  const [retailerHierarchyAvailable, setRetailerHierarchyAvailable] = React.useState(false);
  const [defaultValuesLoaded, setDefaultValuesLoaded] = React.useState(false);
  const initializedDefaultsRef = React.useRef(false);

  const loggedInUserType = resolveLoggedInUserType();
  const workspaceOptionsCount = Array.isArray(storeDetails[WORKSPACE_OPTIONS_KEY])
    ? storeDetails[WORKSPACE_OPTIONS_KEY].length
    : 0;
  const retailerSelectionOptionsCount = Array.isArray(storeDetails[RETAILER_SELECTION_OPTIONS_KEY])
    ? storeDetails[RETAILER_SELECTION_OPTIONS_KEY].length
    : 0;
  const storeNames = React.useMemo(
    () => resolveStoreNames({ mode, behaviorMode, fetchListFor, usedFor, storeDetails }),
    [behaviorMode, fetchListFor, mode, storeDetails, usedFor]
  );

  const storeOptions = React.useMemo(() => toStoreOptions(storeNames), [storeNames]);

  //store object with reference to all related info 
  const storeRecordsForOptions = React.useMemo(
    () => resolveStoreRecordsForOptions({ behaviorMode, storeDetails }),
    [behaviorMode, storeDetails]
  );

  // to be used for grouped multiselect version of store filter.
  const storeOptionsWithRootRetailer = React.useMemo(
    () => toStoreOptionsWithRootRetailer(storeNames, storeRecordsForOptions),
    [storeNames, storeRecordsForOptions]
  );

  // Hierarchy is shown only when the current option source can map every visible store to a retailer root.
  const shouldUseRetailerHierarchy = shouldUseRetailerStoreHierarchy({
    isBrand: mode === STORE_FILTER_MODES.BRAND,
    retailerHierarchyAvailable,
    storeOptions: storeOptionsWithRootRetailer,
  });

  const shouldInitializeRedux = behaviorMode !== STORE_FILTER_MODES.CAMPAIGN;
  const shouldUpdateDateRange = behaviorMode === STORE_FILTER_MODES.PERIOD_BASED;

  const updateDateRange = React.useCallback(
    (selectedStoreNames) => {
      if (!shouldUpdateDateRange || typeof storeDetails.store_dates === "undefined") return;

      dispatchAction(updateStateData(resolveDateRangeFromStores(selectedStoreNames, storeDetails.store_dates)));
    },
    [dispatchAction, shouldUpdateDateRange, storeDetails.store_dates]
  );

  React.useEffect(() => {
    let shouldApplyResult = true;

    setRetailerAccessLoaded(false);

    const checkRetailerAccess = async () => {
      try {
        const retailerAccessInfo = await loadRetailerAccessAvailability(loggedInUserType);

        if (shouldApplyResult) {
          setRetailerHierarchyAvailable(retailerAccessInfo.available === true);
        }
      } catch {
        if (shouldApplyResult) {
          setRetailerHierarchyAvailable(false);
        }
      } finally {
        if (shouldApplyResult) {
          setRetailerAccessLoaded(true);
        }
      }
    };

    if (loggedInUserType === "") {
      setRetailerHierarchyAvailable(false);
      setRetailerAccessLoaded(true);
    } else {
      checkRetailerAccess();
    }

    return () => {
      shouldApplyResult = false;
    };
  }, [
    loggedInUserType,
    // Retailer options are often hydrated by BottomNav after this filter mounts.
    // Re-run the shared availability check when those caches become available.
    retailerSelectionOptionsCount,
    storesLoaded,
    workspaceOptionsCount,
  ]);

  React.useEffect(() => {
    let isMounted = true;

    initializedDefaultsRef.current = false;
    setStoresLoaded(false);
    setDefaultValuesLoaded(false);
    setBatchLoadingStatus(true);
    // Drop the previous retailer's dates and store selection so the dashboard
    // and dropdown wait for the new defaults instead of showing leftover values.
    const keysToClear = [];
    if (shouldUpdateDateRange === true) {
      keysToClear.push("store_dates");
    }
    if (typeof reduxKeywordToSaveValue !== "undefined") {
      keysToClear.push(reduxKeywordToSaveValue);
    }
    if (keysToClear.length !== 0) {
      dispatchAction(deleteItems(keysToClear));
    }

    const fetchStores = async () => {
      try {
        await loadRequestedStores(behaviorMode, usedFor);
      } finally {
        if (isMounted) {
          setStoresLoaded(true);
        } else {
          setBatchLoadingStatus(false);
        }
      }
    };

    fetchStores();

    return () => {
      isMounted = false;
    };
  }, [behaviorMode, dispatchAction, reduxKeywordToSaveValue, shouldUpdateDateRange, usedFor]);

  React.useEffect(() => {
    if (!storesLoaded || initializedDefaultsRef.current) return;

    // After a retailer change we delete store_dates first. Wait only for that
    // gap. An empty array means the API really returned no stores.
    if (shouldUpdateDateRange === true && typeof storeDetails.store_dates === "undefined") {
      return;
    }

    initializedDefaultsRef.current = true;

    const defaultStoreNames = uniqueStoreNames(storeNames);
    const defaultSelectedStoreOptions = toStoreOptions(defaultStoreNames);

    setStoreSelection(defaultStoreNames);
    setStoreNameOptionsSelected(defaultSelectedStoreOptions);
    updateDateRange(defaultStoreNames);

    if (shouldInitializeRedux && typeof reduxKeywordToSaveValue !== "undefined") {
      dispatchAction(
        updateStateData({
          [reduxKeywordToSaveValue]: {
            ...storeDetails[reduxKeywordToSaveValue],
            store_ids: resolveStoreIds({
              selectedStoreNames: defaultStoreNames,
              behaviorMode,
            }),
            selected_store_names: defaultSelectedStoreOptions,
          },
        })
      );
    }

    setDefaultValuesLoaded(true);
    setBatchLoadingStatus(false);
  }, [
    behaviorMode,
    dispatchAction,
    reduxKeywordToSaveValue,
    shouldInitializeRedux,
    shouldUpdateDateRange,
    storeDetails,
    storeNames,
    storesLoaded,
    updateDateRange,
  ]);

  if (!defaultValuesLoaded || !retailerAccessLoaded) {
    return <></>;
  }

  // Use the list we just defaulted to, not Redux leftovers from the last retailer
  const defaultSelectedStoreOptions = storeNameOptionSelected;

  const handleStoreSelectionChange = (selectedOption) => {
    const selectedStoreNames = resolveSelectedStoreNames(selectedOption, storeNames);

    setChangesMade(true);
    setStoreSelection(selectedStoreNames);
    setStoreNameOptionsSelected(toStoreOptions(selectedStoreNames));
  };

  const handleStoreSelectionBlur = () => {
    if (storesSelection.length === 0 || changesMade !== true) return;

    updateDateRange(storesSelection);

    if (typeof reduxKeywordToSaveValue !== "undefined") {
      dispatchAction(
        updateStateData({
          [reduxKeywordToSaveValue]: {
            ...storeDetails[reduxKeywordToSaveValue],
            store_ids: resolveStoreIds({
              selectedStoreNames: storesSelection,
              behaviorMode,
            }),
            selected_store_names: storeNameOptionSelected,
          },
        })
      );
    }

    setChangesMade(false);
  };

  if (shouldUseRetailerHierarchy === true) {
    return (
      <GroupedMultiSelect
        enableFullWidth={false}
        elemId={`${reduxKeywordToSaveValue || usedFor}_store_filter`}
        label={"Store"}
        options={storeOptionsWithRootRetailer}
        defaultVal={defaultSelectedStoreOptions}
        groupParam={ROOT_RETAILER_GROUP_PARAM}
        selectionCheck={(option, value) => option.label === value.label}
        showGroupNames={true}
        showSelectionSummary={true}
        usedFor="storeFilter"
        onChangeCall={handleStoreSelectionChange}
        onBlurCall={handleStoreSelectionBlur}
      />
    );
  }

  return (
    <MultiSelect
      enableFullWidth={false}
      label={"Store"}
      options={storeOptions}
      defaultVal={defaultSelectedStoreOptions}
      selectionCheck={(option, value) => option.label === value.label}
      usedFor="storeFilter"
      onChangeCall={handleStoreSelectionChange}
      onBlurCall={handleStoreSelectionBlur}
    />
  );
};

const PeriodBasedStoreFilter = (props) => (
  <StoreFilterSelect {...props} behaviorMode={STORE_FILTER_MODES.PERIOD_BASED} />
);

const CampaignStoreFilter = (props) => (
  <StoreFilterSelect {...props} behaviorMode={STORE_FILTER_MODES.CAMPAIGN} />
);

const IndependentStoreFilter = (props) => (
  <StoreFilterSelect {...props} behaviorMode={STORE_FILTER_MODES.INDEPENDENT} />
);

const StoreFilter = ({ usedFor, fetchListFor, reduxKeywordToSaveValue }) => {
  const storeDetails = useSelector((state) => state.userSelections);
  const userRole = storeDetails?.brand_report_view === true ? { user_role: "Brand" } : checkUserRole();
  const behaviorMode = resolveBehaviorMode({ usedFor, fetchListFor });
  const mode = userRole.user_role === "Brand" ? STORE_FILTER_MODES.BRAND : behaviorMode;
  const filterProps = {
    fetchListFor,
    mode,
    reduxKeywordToSaveValue,
    usedFor,
  };

  if (behaviorMode === STORE_FILTER_MODES.CAMPAIGN) {
    return <CampaignStoreFilter {...filterProps} />;
  }

  if (behaviorMode === STORE_FILTER_MODES.INDEPENDENT) {
    return <IndependentStoreFilter {...filterProps} />;
  }

  return <PeriodBasedStoreFilter {...filterProps} />;
};

export default StoreFilter;
